import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useIntakeFormStore,
  memberInfoSchema,
  TMemberInfoSchema,
  SUPPORTED_GENDER_VALUES,
  SUPPORTED_RACE_VALUES,
  SUPPORTED_ETHNICITY_VALUES,
  SUPPORTED_HAVE_INTERNET_VALUES,
} from "../intakeFormState";
// UI
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import { TextInput } from "../../../components/TextInput/TextInput";
import { RadioSelectInput } from "../../../components/RadioSelectInput/RadioSelectInput";
import { NextButton } from "../NextButton";
// controller (from layout)
import { useOutletContext } from "react-router-dom";
import type { IntakeOutletContext } from "../IntakeFormLayout";

export const IntakeFormMemberInfoScreen = React.memo(() => {
  const { memberInfo, handleMemberInfoSubmit } = useIntakeFormStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TMemberInfoSchema>({
    resolver: yupResolver(memberInfoSchema),
    reValidateMode: "onBlur",
    mode: "onBlur",
    defaultValues: memberInfo,
  });

  const outletContext: IntakeOutletContext = useOutletContext();
  const { onSubmit: afterSubmit } = outletContext;

  const onSubmit = useCallback(
    (data: TMemberInfoSchema) => {
      console.log("data", data);
      // TODO: NEED TO CHECK CONSENT STATUS AND FLASH A TOAST IF THEY DON'T CONSENT
      handleMemberInfoSubmit(data);
      afterSubmit();
    },
    [handleMemberInfoSubmit, afterSubmit],
  );

  return (
    <IntakeFormScreenContainer title={t("intakeMemberInfo.header")}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          {...register("firstName")}
          isRequired
          label={t("intakeMemberInfo.firstName")}
          hookFormFieldError={errors.firstName}
        />

        <div className="mt-1">
          <TextInput
            {...register("lastName")}
            isRequired
            label={t("intakeMemberInfo.lastName")}
            hookFormFieldError={errors.lastName}
          />
        </div>

        <div className="mt-1">
          <TextInput
            {...register("dateOfBirth")}
            isRequired
            label={t("intakeMemberInfo.dateOfBirth")}
            // NOTE: browsers support this, it's built in!! returns str `1997-06-24`, react-hook-form works gracefully
            type="date"
            hookFormFieldError={errors.dateOfBirth}
          />
        </div>

        <div className="mt-4 flex flex-col">
          <RadioSelectInput
            {...register("gender")}
            label={t("intakeMemberInfo.gender")}
            isRequired
            choices={SUPPORTED_GENDER_VALUES.map((v) => {
              return { label: t(`intakeMemberInfo.genderChoices.${v}`), value: v };
            })}
            hookFormFieldError={errors.gender}
          />
        </div>

        <div className="mt-4 flex flex-col">
          <RadioSelectInput
            {...register("race")}
            label={t("intakeMemberInfo.race")}
            isRequired
            choices={SUPPORTED_RACE_VALUES.map((v) => {
              return { label: t(`intakeMemberInfo.raceChoices.${v}`), value: v };
            })}
            hookFormFieldError={errors.race}
          />
        </div>

        <div className="mt-4 flex flex-col">
          <RadioSelectInput
            {...register("ethnicity")}
            label={t("intakeMemberInfo.ethnicity")}
            isRequired
            choices={SUPPORTED_ETHNICITY_VALUES.map((v) => {
              return { label: t(`intakeMemberInfo.ethnicityChoices.${v}`), value: v };
            })}
            hookFormFieldError={errors.ethnicity}
          />
        </div>

        <div className="mt-4 flex flex-col">
          <RadioSelectInput
            {...register("haveInternet")}
            label={t("intakeMemberInfo.haveInternet")}
            isRequired
            choices={SUPPORTED_HAVE_INTERNET_VALUES.map((v) => {
              return { label: t(`intakeMemberInfo.haveInternetChoices.${v}`), value: v };
            })}
            hookFormFieldError={errors.haveInternet}
          />
        </div>

        <NextButton />
      </form>
    </IntakeFormScreenContainer>
  );
});

IntakeFormMemberInfoScreen.displayName = "IntakeFormMemberInfoScreen";
